import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { tenantApi } from '../../api/endpoints';
import { isApiError } from '../../api/http';
import type { Company, CompanyDetails, CompanyDetailsState, CompanyVatCheck } from '../../api/types';
import { numberLanguage, useI18n } from '../../i18n';
import { checkCompanyNumber, compactNumber, numberMessage, numberShape, vatPrefix, type NumberKind } from '@smbsuite/company-numbers';
import { TERMS_VERSION } from '@smbsuite/legal';
import { Checkbox, errorMessage, Form, Icon, SelectField, TextField, useToast } from '../../ui';
import { useBotCheck } from '../../ui/bot-check';
import { countryOptions } from '../crm/address/format';
import { splitRegisteredAddress } from '../crm/address/registered';
import { LegalConsent } from '../legal/LegalConsent';

// A company's details (TF-79), one form for registering a company and for completing or changing its
// details: the name it goes by, its country, its registration or taxpayer number (required to register,
// TF-231) and VAT number, each checked against its country's rule on leaving the field, legal name and
// address, and optionally how to reach it for invoices. A number already registered is said so, naming nobody, with a way to ask to join; a VAT
// number is checked in VIES, which can fill the legal name and address.

type Draft = {
  name: string;
  country: string;
  registrationNo: string;
  vatNo: string;
  legalName: string;
  line1: string;
  postalCode: string;
  city: string;
  invoiceEmail: string;
  phone: string;
  website: string;
};

const formOf = (details: CompanyDetails | null | undefined, name = ''): Draft => ({
  name,
  country: details?.country ?? '',
  registrationNo: details?.registrationNo ?? '',
  vatNo: details?.vatNo ?? '',
  legalName: details?.legalName ?? '',
  line1: details?.legalAddress?.line1 ?? '',
  postalCode: details?.legalAddress?.postalCode ?? '',
  city: details?.legalAddress?.city ?? '',
  invoiceEmail: details?.invoiceEmail ?? '',
  phone: details?.phone ?? '',
  website: details?.website ?? '',
});

const detailsOf = (form: Draft): Partial<CompanyDetails> => ({
  country: form.country || null,
  registrationNo: form.registrationNo.trim() || null,
  vatNo: form.vatNo.trim() || null,
  legalName: form.legalName.trim() || null,
  legalAddress: form.line1.trim() || form.postalCode.trim() || form.city.trim() ? { line1: form.line1.trim(), postalCode: form.postalCode.trim(), city: form.city.trim() } : null,
  invoiceEmail: form.invoiceEmail.trim() || null,
  phone: form.phone.trim() || null,
  website: form.website.trim() || null,
});

type Props =
  | { mode: 'register'; onSaved: (company: Company) => void; onCancel?: () => void }
  | { mode: 'edit'; tenantId: string; initial: CompanyDetailsState; onSaved: (details: CompanyDetailsState) => void; onCancel?: () => void };

export function CompanyDetailsForm(props: Props) {
  const { t, language } = useI18n();
  const toast = useToast();
  const registering = props.mode === 'register';
  const botCheck = useBotCheck('company');
  const [form, setForm] = useState<Draft>(() => formOf(registering ? null : props.initial));
  const [taken, setTaken] = useState(false);
  // The demo company's number (TF-79): no customer's, and nobody to ask to join.
  const [reserved, setReserved] = useState(false);
  // The terms of service, the privacy policy and the data processing agreement, accepted for the company (TF-256).
  const [accepted, setAccepted] = useState(false);
  // The number check's hourly limit reached (TF-216): said under the numbers; the server checks at Create.
  const [checkLimited, setCheckLimited] = useState(false);
  const [asked, setAsked] = useState(false);
  const [vat, setVat] = useState<CompanyVatCheck | null>(null);
  const countries = useMemo(() => countryOptions(language), [language]);
  // The numbers as stored: one kept from before the country rules is not checked again until changed.
  const kept = registering ? null : props.initial;
  /** What is wrong with a number, in words, or "" (TF-231): the same rules the server keeps. */
  const numberProblem = (kind: NumberKind, value: string) => {
    const keptValue = kind === 'registration' ? kept?.registrationNo : kept?.vatNo;
    if (!value.trim() || (keptValue && compactNumber(value) === keptValue)) return '';
    const check = checkCompanyNumber(kind, form.country || null, value);
    return check.ok ? '' : numberMessage(kind, form.country || null, check, numberLanguage(language));
  };
  const set = (key: keyof Draft) => (value: string) => {
    // A VAT number starts with the country's prefix: typing its digits first puts the prefix in front.
    if (key === 'vatNo' && form.country && !form.vatNo && /^\d/.test(value)) value = `${vatPrefix(form.country)}${value}`;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === 'registrationNo' || key === 'vatNo' || key === 'country') {
      setTaken(false);
      setReserved(false);
    }
    if (key === 'vatNo' || key === 'country') setVat(null);
  };
  const numbers = () => ({ country: form.country, ...(form.registrationNo.trim() ? { registrationNo: form.registrationNo } : {}), ...(form.vatNo.trim() ? { vatNo: form.vatNo } : {}) });

  // On leaving a number field: is it registered already? (Only when registering: an edit checks on save.)
  // Not silent when it fails (TF-216): over the limit it says so; anything else is asked once more, quietly,
  // and then left to the server, which checks the number again when the company is created.
  const check = useMutation({
    mutationFn: () => tenantApi.numberCheck(numbers()),
    retry: (failures, error) => failures < 1 && !(isApiError(error) && error.code === 'TOO_MANY_CHECKS'),
    retryDelay: 500,
    onSuccess: (result) => {
      setTaken(result.taken);
      setReserved(result.reserved === true);
      setCheckLimited(false);
    },
    onError: (error) => setCheckLimited(isApiError(error) && error.code === 'TOO_MANY_CHECKS'),
  });
  const vatCheck = useMutation({
    mutationFn: () => tenantApi.vatCheck(form.country, form.vatNo),
    onSuccess: (found) => {
      setVat(found);
      // The number first (TF-212): a valid one names the company while its name is still empty; the
      // short name people will see can still be changed.
      if (registering && found.valid && found.name) setForm((current) => (current.name.trim() ? current : { ...current, name: found.name! }));
    },
    onError: () => undefined,
  });
  const join = useMutation({
    mutationFn: () => tenantApi.joinRequest(numbers()),
    onSuccess: () => setAsked(true),
    onError: (error) => toast.error(errorMessage(error, t('common.somethingWrong'))),
  });
  const leaveNumber = (which: 'registrationNo' | 'vatNo') => {
    // A number that does not fit its country's rule is said at once, and nobody is asked about it.
    if (!form.country || !form[which].trim() || numberProblem(which === 'registrationNo' ? 'registration' : 'vat', form[which])) return;
    if (registering) check.mutate();
    if (which === 'vatNo') vatCheck.mutate();
  };
  const fillFromVies = () => {
    if (!vat?.valid) return;
    const address = vat.address ? splitRegisteredAddress(vat.address, form.country) : null;
    setForm((current) => ({
      ...current,
      legalName: vat.name ?? current.legalName,
      ...(address ? { line1: [address.line1, address.line2].filter(Boolean).join(', '), postalCode: address.postalCode ?? current.postalCode, city: address.city ?? current.city } : {}),
    }));
  };

  const save = useMutation({
    mutationFn: async (): Promise<{ kind: 'company'; company: Company } | { kind: 'details'; details: CompanyDetailsState }> => {
      if (props.mode === 'register') {
        const input = { name: form.name.trim(), ...detailsOf(form), turnstileToken: botCheck.token ?? undefined, acceptTerms: TERMS_VERSION };
        return { kind: 'company', company: await tenantApi.createCompany(input) };
      }
      return { kind: 'details', details: (await tenantApi.saveCompanyDetails(props.tenantId, detailsOf(form))).details };
    },
    onSuccess: (result) => {
      if (result.kind === 'company' && props.mode === 'register') props.onSaved(result.company);
      if (result.kind === 'details' && props.mode === 'edit') props.onSaved(result.details);
    },
    // Any other failure shows in the form, at the field it names.
    onError: (error) => {
      if (isApiError(error) && error.code === 'NUMBER_TAKEN') setTaken(true);
      if (isApiError(error) && error.code === 'NUMBER_RESERVED') setReserved(true);
      // The bot check's token answers once: a refused registration gets a new check (TF-112).
      botCheck.reset();
    },
  });

  // Registering takes the registration or taxpayer number; a company kept from before needs one of the two.
  const hasNumber = registering ? Boolean(form.registrationNo.trim()) : Boolean(form.registrationNo.trim() || form.vatNo.trim());
  const numbersFit = !numberProblem('registration', form.registrationNo) && !numberProblem('vat', form.vatNo);
  const ready =
    (!registering || form.name.trim()) &&
    hasNumber &&
    numbersFit &&
    form.country &&
    form.legalName.trim() &&
    form.line1.trim() &&
    form.city.trim() &&
    (!registering || botCheck.token || botCheck.failed) &&
    (!registering || accepted) &&
    !taken &&
    !reserved;
  // A number already registered has its own banner; it is about no one field.
  const failure = isApiError(save.error) && (save.error.code === 'NUMBER_TAKEN' || save.error.code === 'NUMBER_RESERVED') ? null : save.error;

  return (
    <Form
      className="company-details-form stack"
      error={failure}
      onSubmit={() => {
        if (ready) save.mutate();
      }}
    >
      <SelectField
        id="company-country"
        name="country"
        label={t('company.country')}
        value={form.country}
        onChange={set('country')}
        required
        placeholder={t('company.chooseCountry')}
        options={countries.map((country) => ({ value: country.code, label: country.name }))}
      />

      <fieldset className="number-fields">
        <legend>{t('company.numbers')}</legend>
        <p className="small muted m-0">{t('company.numbersHint')}</p>
        <div className="form-row">
          <TextField
            id="company-registration"
            name="registrationNo"
            label={t('company.registrationOrTaxpayerNo')}
            value={form.registrationNo}
            onChange={set('registrationNo')}
            onBlur={() => leaveNumber('registrationNo')}
            validate={(value) => numberProblem('registration', value)}
            required={registering}
            maxLength={40}
            placeholder={form.country ? numberShape('registration', form.country, numberLanguage(language)) : undefined}
          />
          <div>
            <TextField
              id="company-vat"
              name="vatNo"
              label={t('company.vatNo')}
              value={form.vatNo}
              onChange={set('vatNo')}
              onBlur={() => leaveNumber('vatNo')}
              validate={(value) => numberProblem('vat', value)}
              maxLength={40}
              placeholder={form.country ? numberShape('vat', form.country, numberLanguage(language)) : 'LV40003123456'}
            />
            {vat?.checked && vat.valid && (
              <p className="small vat-valid" role="status">
                <Icon name="check_circle" size={18} /> {t('company.vatValid')}
                {(vat.name || vat.address) && (
                  // An action the app proposes: Material's assist chip (TF-96).
                  <button type="button" className="chip-assist" onClick={fillFromVies}>
                    <Icon name="edit" size={18} />
                    {t('company.fillFromVies')}
                  </button>
                )}
              </p>
            )}
            {vat?.checked && !vat.valid && (
              <p className="small text-warning" role="status">
                {t('company.vatInvalid')}
              </p>
            )}
            {vat && !vat.checked && vat.reason === 'unavailable' && (
              <p className="small muted" role="status">
                {t('company.vatUnavailable')}
              </p>
            )}
          </div>
        </div>
        {checkLimited && !taken && !reserved && (
          <p className="small muted m-0" role="status">
            {t('company.checkLimited')}
          </p>
        )}
        {reserved && (
          <div className="banner banner-warning number-reserved" role="alert">
            <Icon name="warning" className="icon-lead" />
            <p className="m-0 grow">{t('company.numberReserved')}</p>
          </div>
        )}
        {taken && (
          <div className="banner banner-warning number-taken" role="alert">
            <Icon name="warning" className="icon-lead" />
            <div className="grow">
              <p className="m-0">{t('company.numberTaken')}</p>
              {registering && (
                <p className="m-0 mt-2">
                  {asked ? (
                    <span>{t('company.joinAsked')}</span>
                  ) : (
                    <button type="button" className="btn btn-sm" disabled={join.isPending} onClick={() => join.mutate()}>
                      {t('company.askToJoin')}
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </fieldset>

      {/* Country and numbers first (TF-212): a valid VAT number fills the name in. */}
      {registering && (
        <TextField
          id="company-name"
          name="name"
          label={t('company.nameLabel')}
          value={form.name}
          onChange={set('name')}
          required
          maxLength={200}
          autoComplete="organization"
          hint={t('company.nameHint')}
        />
      )}

      <fieldset>
        <legend>{t('company.legal')}</legend>
        <TextField id="company-legal-name" name="legalName" label={t('company.legalName')} value={form.legalName} onChange={set('legalName')} maxLength={200} required />
        {/* The address is one property of the API: its errors show at the street. */}
        <TextField
          id="company-street"
          name="legalAddress"
          label={t('company.street')}
          value={form.line1}
          onChange={set('line1')}
          maxLength={300}
          required
          autoComplete="address-line1"
        />
        <div className="form-row">
          <TextField id="company-postal" label={t('company.postalCode')} value={form.postalCode} onChange={set('postalCode')} maxLength={20} autoComplete="postal-code" />
          <TextField id="company-city" label={t('company.city')} value={form.city} onChange={set('city')} maxLength={100} required autoComplete="address-level2" />
        </div>
      </fieldset>

      <fieldset>
        <legend>{t('company.contact')}</legend>
        <div className="form-row">
          <TextField id="company-invoice-email" name="invoiceEmail" label={t('company.invoiceEmail')} type="email" value={form.invoiceEmail} onChange={set('invoiceEmail')} maxLength={320} />
          <TextField id="company-phone" name="phone" label={t('company.phone')} type="tel" value={form.phone} onChange={set('phone')} maxLength={50} autoComplete="tel" />
        </div>
        <TextField id="company-website" name="website" label={t('company.website')} type="url" value={form.website} onChange={set('website')} maxLength={300} placeholder="https://" />
      </fieldset>

      {/* The company accepts the terms, and with them the data processing agreement (TF-255, TF-256). New tabs keep the form. */}
      {registering && (
        <Checkbox
          id="company-accept-terms"
          checked={accepted}
          onChange={setAccepted}
          required
          className="choice-consent"
          label={<LegalConsent forCompany />}
        />
      )}
      {/* Registering a company: Cloudflare's check that a person does it (TF-112). */}
      {registering && botCheck.widget}
      <div className="btn-row">
        <button type="submit" className="btn btn-primary" disabled={!ready || save.isPending}>
          {registering ? t('company.create') : t('common.save')}
        </button>
        {props.onCancel && (
          <button type="button" className="btn" onClick={props.onCancel}>
            {t('common.cancel')}
          </button>
        )}
      </div>
    </Form>
  );
}
